import { useState, useEffect, useContext, createContext, FormEvent } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Lock, AlertCircle, LogOut } from "lucide-react"

const AUTH_KEY = "uvx_dashboard_auth"
const AUTH_EXPIRY_DAYS = 7

// Per-user PINs — add users here
const USERS: Record<string, { pin: string; role: string }> = {
  riz: { pin: import.meta.env.VITE_PIN_RIZ || "riz123", role: "admin" },
  monika: { pin: import.meta.env.VITE_PIN_MONIKA || "monika123", role: "admin" },
  mike: { pin: import.meta.env.VITE_PIN_MIKE || "mike123", role: "admin" },
  anja: { pin: import.meta.env.VITE_PIN_ANJA || "anja123", role: "admin" },
  jure: { pin: import.meta.env.VITE_PIN_JURE, role: "admin" },
  "jure@udonis.co": { pin: import.meta.env.VITE_PIN_JURE, role: "admin" },
}

// Also support legacy single password as fallback
const LEGACY_PASSWORD = import.meta.env.VITE_DASHBOARD_PASSWORD

// Force re-login when auth format changes
const AUTH_VERSION = 2

interface AuthUser {
  name: string
  role: string
}

const AuthContext = createContext<AuthUser | null>(null)

export function useAuthUser(): AuthUser | null {
  return useContext(AuthContext)
}

export function getStoredUser(): AuthUser | null {
  try {
    const stored = localStorage.getItem(AUTH_KEY)
    if (!stored) return null
    const { user, expiry, version } = JSON.parse(stored)
    if (version !== AUTH_VERSION) {
      localStorage.removeItem(AUTH_KEY)
      return null
    }
    if (expiry > Date.now() && user?.name) return user
    return null
  } catch {
    localStorage.removeItem(AUTH_KEY)
    return null
  }
}

interface PasswordGateProps {
  children: React.ReactNode
}

export function PasswordGate({ children }: PasswordGateProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [name, setName] = useState("")
  const [pin, setPin] = useState("")
  const [error, setError] = useState("")

  useEffect(() => {
    const stored = getStoredUser()
    if (stored) setUser(stored)
    setIsLoading(false)
  }, [])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError("")

    const username = name.trim().toLowerCase()

    // Check per-user PINs first
    const userConfig = USERS[username]
    if (userConfig && pin === userConfig.pin) {
      const authUser: AuthUser = { name: username, role: userConfig.role }
      const expiry = Date.now() + (AUTH_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
      localStorage.setItem(AUTH_KEY, JSON.stringify({ user: authUser, expiry, version: AUTH_VERSION }))
      setUser(authUser)
      return
    }

    // Fallback: legacy single password (logs as "unknown")
    if (LEGACY_PASSWORD && pin === LEGACY_PASSWORD) {
      const authUser: AuthUser = { name: username || "unknown", role: "admin" }
      const expiry = Date.now() + (AUTH_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
      localStorage.setItem(AUTH_KEY, JSON.stringify({ user: authUser, expiry, version: AUTH_VERSION }))
      setUser(authUser)
      return
    }

    setError("Invalid name or PIN")
    setPin("")
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (user) {
    return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-lg">UVX Dashboard</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Sign in to continue
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                className="text-center"
              />
              <Input
                type="password"
                placeholder="PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="text-center"
                inputMode="numeric"
              />
              {error && (
                <div className="flex items-center justify-center gap-1.5 text-destructive text-xs">
                  <AlertCircle className="h-3 w-3" />
                  {error}
                </div>
              )}
            </div>
            <Button type="submit" className="w-full">
              Sign In
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export function LogoutButton() {
  const user = useAuthUser()
  if (!user) return null
  return (
    <button
      onClick={() => { localStorage.removeItem(AUTH_KEY); window.location.reload() }}
      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
    >
      <span className="font-medium">{user.name}</span>
      <LogOut className="h-3 w-3" />
    </button>
  )
}
